export interface Route {
  name: string;
  params: Record<string, string>;
  query: URLSearchParams;
}

const ROUTES: [string, RegExp][] = [
  ['home', /^\/$/],
  ['episodes', /^\/episodes\/?$/],
  ['episode', /^\/episodes\/(?<slug>[a-z0-9-]{1,64})\/?$/],
  ['map', /^\/map\/?$/],
  ['characters', /^\/characters\/?$/],
  ['character', /^\/characters\/(?<slug>[a-z0-9-]{1,64})\/?$/],
  ['lore', /^\/lore\/?$/],
  ['loreEntry', /^\/lore\/(?<slug>[a-z0-9-]{1,64})\/?$/],
  ['about', /^\/about\/?$/],
  ['parents', /^\/parents\/?$/],
  ['parentsSignIn', /^\/parents\/sign-in\/?$/],
  ['parentsSignUp', /^\/parents\/sign-up\/?$/],
  ['family', /^\/parents\/family\/?$/],
  ['pair', /^\/pair\/?$/],
  ['fun', /^\/fun\/?$/],
  ['funRedstone', /^\/fun\/redstone\/?$/],
  ['funBlueprints', /^\/fun\/blueprints\/?$/],
  ['funMinecarts', /^\/fun\/minecarts\/?$/],
  ['funExpedition', /^\/fun\/expedition\/?$/],
  ['funCharacter', /^\/fun\/character\/?$/],
  ['funSkins', /^\/fun\/skin-studio\/?$/],
];

export function match(url: URL): Route {
  for (const [name, re] of ROUTES) {
    const m = re.exec(url.pathname);
    if (m) return { name, params: { ...m.groups }, query: url.searchParams };
  }
  return { name: 'notFound', params: {}, query: url.searchParams };
}

/** Keeps preview/fixture flags on internal navigation. */
export function withSessionParams(href: string): string {
  const cur = new URLSearchParams(location.search);
  const url = new URL(href, location.origin);
  for (const k of ['preview', 'fixtures'])
    if (cur.has(k) && !url.searchParams.has(k)) url.searchParams.set(k, cur.get(k)!);
  return url.pathname + url.search + url.hash;
}

/** Intercepts same-origin link clicks, including those inside shadow roots. */
export function startRouter(onChange: (r: Route) => void) {
  const emit = () => onChange(match(new URL(location.href)));
  document.addEventListener('click', (e) => {
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey)
      return;
    const a = e.composedPath().find((n): n is HTMLAnchorElement => n instanceof HTMLAnchorElement);
    if (!a || a.target || a.hasAttribute('download') || a.origin !== location.origin) return;
    if (a.pathname.startsWith('/releases/') || a.pathname.startsWith('/admin')) return;
    e.preventDefault();
    navigate(a.pathname + a.search + a.hash);
  });
  window.addEventListener('popstate', emit);
  emit();
  return emit;
}

let emitter: (() => void) | undefined;
export function navigate(href: string, replace = false) {
  const target = withSessionParams(href);
  if (replace) history.replaceState(null, '', target);
  else history.pushState(null, '', target);
  emitter?.();
}
export function setEmitter(fn: () => void) {
  emitter = fn;
}
